import { Localization, SessionContext } from "@viamap/viamap2-common";
import JSZip from "jszip";
import { useContext, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Spinner } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { utils as XlsxUtils, write as writeXlsx } from "xlsx";
import { LayerInfo } from "src/common/managers/Types";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { DC } from "src/dataCollector/DataCollector";
import { ExportData } from "src/managers/ExportData";
import { PropertyInformation } from "src/managers/PropertyInformation";
import { getServitutDownloadFileName } from "src/managers/ServitutDownloadNaming";
import {
   fetchTingbogDocumentBlob,
   getTingbogDownloadTarget,
   isTingbogElectronicDocument,
} from "src/managers/TingbogDownload";
import {
   addServitutPackageToZip,
   isEligibleDigitalServitut,
   type ServitutElectronicPackageServitut,
} from "src/propertyInfoTemplates/TingbogServitutBatch";
import { cutPdfToSelectedPages } from "src/modules/ANNModule/ANNPDFCutter";
import { createANNLLMAnalyzer, getANNLLMAnalyzer, type LLMAnalysisResult, type LLMAnalysisResultItem, type LLMMetadata } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { normalizeServitutIdentity } from "src/modules/ANNModule/ANNServitutIdentity";
import { AnnotationContextType } from "src/modules/ANNModule/ANNTypes";
import { MapitStateContext } from "src/states/MapitState";
import { closeWindow, ManagedWindow, openWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { GlassButton, GlassButtonRow } from "./GlassButtons";
import { GlassInputControl, GlassInputGroup } from "./GlassInput";
import { message } from "./Message";
import { VLine } from "./MitGlassComponents";
import { hentServitutDataOgBeliggenhed, SFEAndColorList, type ServitutRapportBfeData, type SFEColorEntry } from "./ServitutRapportData";
import { aktIdFormat } from "./DatarapportShared";
import "./ServitutRedegoerelseRapport.css";

type ServitutRedegoerelsePreparedAkt = {
   document: string;
   aktId: string;
   documentUrl: string;
   source: "servitutAkt" | "ovrigAkt";
   servitutKeys: string[];
};

/** Fremdrift ved ZIP-eksport (Excel → hvert dokument → komprimering). */
export type ServitutRedegoerelseExportProgress = {
   completedSteps: number;
   totalSteps: number;
   stage: "spreadsheet" | "document" | "compress";
   activeRowKey: string | null;
   /** 1-based indeks blandt dokumenter (kun ved stage document). */
   documentIndex?: number;
   documentTotal?: number;
};

/** Legacy indskannede akt-Samlinger kræver LLM + sidevalg; digitale tingbogsdokumenter pakkes ved eksport som i ejendomsinfo (akt + SERVIMAP + bilag). */
export type ServitutRedegoerelseRowHandling = "legacyLlmPdf" | "electronicZipPackage";

type ServitutMatrixRow = {
   key: string;
   servitut: any;
   handling: ServitutRedegoerelseRowHandling;
   displayAktId: string;
   datoLoebenr: string;
   priority: string;
   summary: string;
   analysisAktId: string;
   includedBfes: Set<number>;
   pdfStatus: "pending" | "ready" | "missing" | "failed";
   pdfSource?: ServitutRedegoerelsePreparedAkt["source"];
   aktDocument?: string;
   aktId?: string;
   documentUrl?: string;
   analysisItem?: LLMAnalysisResultItem;
   selectedPages?: number[];
   exportFileName?: string;
};

type ServitutRedegoerelsePayload = {
   metadata: LLMMetadata[];
   caseDescription?: string;
   analysisResult?: LLMAnalysisResult;
   matrix: {
      columns: number[];
      columnLabels?: Record<string, string>;
      uniqueAktCount: number;
      rows: {
         key: string;
         /** Standard: eksisterende rapporter — behandles som scanned akt + LLM. */
         handling?: ServitutRedegoerelseRowHandling;
         electronicServitut?: ServitutElectronicPackageServitut;
         displayAktId: string;
         datoLoebenr: string;
         priority: string;
         summary: string;
         analysisAktId: string;
         includedBfes: number[];
         pdfStatus: ServitutMatrixRow["pdfStatus"];
         pdfSource?: ServitutRedegoerelsePreparedAkt["source"];
         aktDocument?: string;
         aktId?: string;
         documentUrl?: string;
         analysisItem?: LLMAnalysisResultItem;
         selectedPages?: number[];
         exportFileName?: string;
      }[];
   };
};

type ServitutRedegoerelseWindowParams = {
   payload?: ServitutRedegoerelsePayload;
};

type ServitutBfeHeaderStyle = CSSProperties & {
   "--servitut-bfe-head-width": string;
   "--servitut-bfe-label-width": string;
   "--servitut-bfe-head-height": string;
};

const SERVITUT_ANALYSIS_CONCURRENCY = 10;
const SERVITUT_REDEGOERELSE_SESSION_CACHE_KEY = "servitut-redegoerelse:last-report";

type ServitutRedegoerelseSessionCache = {
   payload: ServitutRedegoerelsePayload;
   validatedKeys: string[];
   savedAt: string;
};

function loadServitutRedegoerelseSessionCache(): ServitutRedegoerelseSessionCache | null {
   try {
      const raw = sessionStorage.getItem(SERVITUT_REDEGOERELSE_SESSION_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<ServitutRedegoerelseSessionCache>;
      if (!parsed.payload?.matrix?.rows || !Array.isArray(parsed.validatedKeys)) {
         return null;
      }
      return parsed as ServitutRedegoerelseSessionCache;
   } catch (error) {
      console.warn("[Servitutredegørelse] failed to restore session cache", error);
      return null;
   }
}

function saveServitutRedegoerelseSessionCache(
   payload: ServitutRedegoerelsePayload,
   validatedKeys: Set<string>
) {
   try {
      const cache: ServitutRedegoerelseSessionCache = {
         payload: {
            ...payload,
            // Raw XML/PDF metadata can be large; the restored UI only needs the prepared matrix/results.
            metadata: [],
         },
         validatedKeys: Array.from(validatedKeys),
         savedAt: new Date().toISOString(),
      };
      sessionStorage.setItem(SERVITUT_REDEGOERELSE_SESSION_CACHE_KEY, JSON.stringify(cache));
   } catch (error) {
      console.warn("[Servitutredegørelse] failed to save session cache", error);
   }
}

export function ServitutRedegoerelseRapport(props: {
   currentRapport: string,
   availableRapports: { value: string, label: string }[],
   onSelectRapport: (a: string) => void,
   onComplete?: () => void
}) {
   const { state: mapitState } = useContext(MapitStateContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const [loading, setLoading] = useState(false);

   const usableLayers = useMemo(() => {
      return Object.values(mapitState.layers).filter((layer): layer is LayerInfo => layer.propertiesInGeoJson?.includes("SFEnummer") ?? false);
   }, [mapitState.layers]);

   const [layerId, setLayerId] = useState<number[]>(usableLayers.map((a) => a.layerId));

   useEffect(() => {
      setLayerId((a) => a.filter((b) => usableLayers.map((layer) => layer.layerId).includes(b)));
   }, [mapitState.layers, usableLayers]);

   const colorList = useMemo(() => {
      return SFEAndColorList(
         layerId.map((a) => mapitState.layers[a]).filter(Boolean) as LayerInfo[]
      );
   }, [layerId, mapitState.layers]);

   async function prepareRedegoerelse() {
      setLoading(true);
      try {
         const data = await hentServitutDataOgBeliggenhed(colorList);
         const payload = await prepareServitutRedegoerelsePayload(data, colorList);
         console.log("[Servitutredegørelse] batch-ready payload", payload);
         windowDispatch(openWindow(WindowId.ServitutRedegoerelse, {
            payload,
         }));
         props.onComplete?.();
      } catch (e: unknown) {
         if (e instanceof Error) {
            message.error(Localization.getFormattedText("An error occurred while generating the report. Please try again later or contact support. Error details: {err}", { err: e.message }));
         } else {
            message.error(Localization.getText("An error occurred while generating the report. Please try again later or contact support."));
         }
      } finally {
         setLoading(false);
      }
   }
   return (
      <>
         <ADModalBody>
            <GlassInputGroup>
               <label>{Localization.getText("Document")}</label>
               <GlassInputControl>
                  <select
                     disabled={false}
                     value={props.currentRapport}
                     onChange={(e) => {
                        props.onSelectRapport(e.currentTarget.value);
                     }}
                  >
                     {props.availableRapports.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                     ))}
                  </select>
               </GlassInputControl>
               <label>{Localization.getText("Layers")}</label>
               <Typeahead
                  multiple={true}
                  id="servitutRedegoerelseLayerTypeahead"
                  options={usableLayers.map((a) => ({ id: a.layerId, datasetname: a.datasetname }))}
                  labelKey={"datasetname"}
                  placeholder={Localization.getText("please select") + " " + Localization.getText("layer")}
                  emptyLabel={Localization.getText("No results found")}
                  onChange={(selected) => {
                     setLayerId(selected.map((a: any) => a.id));
                  }}
                  selected={usableLayers.map((a) => ({ id: a.layerId, datasetname: a.datasetname })).filter((a) => layerId.includes(a.id))}
               />
               <label></label>
               <GlassButtonRow>
                  <GlassButton onClick={() => setLayerId(usableLayers.map((a) => a.layerId))}>{Localization.getText("Select All")}</GlassButton>
                  <GlassButton onClick={() => setLayerId([])}>{Localization.getText("Remove All")}</GlassButton>
               </GlassButtonRow>
               <VLine color={"#9d9d9d"} /><VLine color={"#9d9d9d"} />
               <label>{Localization.getText("Properties")} {colorList.length}</label>
               <div style={{ display: "flex", flexWrap: "wrap", gap: "2px" }}>
                  {colorList.map((a) => (
                     <div
                        key={a.sfe}
                        title={a.sfe + ""}
                        style={{ background: a.color, borderRadius: "5px", padding: "3px", display: "flex", justifyContent: "center" }}
                     >
                        <MIBIcon icon="property" style={{ aspectRatio: "1", color: "white" }} />
                     </div>
                  ))}
               </div>
            </GlassInputGroup>
         </ADModalBody>
         <ADModalFooter>
            {loading ? <Spinner /> : (
               <GlassButton disabled={!colorList.length} onClick={prepareRedegoerelse}>
                  {Localization.getText("Generate")}
               </GlassButton>
            )}
         </ADModalFooter>
      </>
   );
}

export function ServitutRedegoerelseWindow(props: { managedWindow: ManagedWindow }) {
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const windowParams = props.managedWindow.windowParams as ServitutRedegoerelseWindowParams | undefined;
   const initialPayload = windowParams?.payload;
   const shouldUseFreshPayload = Boolean(initialPayload);
   const [prepared, setPrepared] = useState<ServitutRedegoerelsePayload | null>(() => {
      if (shouldUseFreshPayload) {
         return initialPayload ?? null;
      }
      return loadServitutRedegoerelseSessionCache()?.payload ?? initialPayload ?? null;
   });
   const [analyzing, setAnalyzing] = useState(false);
   const [validatedKeys, setValidatedKeys] = useState<Set<string>>(() => {
      if (shouldUseFreshPayload) {
         return new Set();
      }
      return new Set(loadServitutRedegoerelseSessionCache()?.validatedKeys ?? []);
   });
   const [openingKey, setOpeningKey] = useState<string | null>(null);
   const [exporting, setExporting] = useState(false);
   const [exportProgress, setExportProgress] = useState<ServitutRedegoerelseExportProgress | null>(null);
   const [caseDescription, setCaseDescription] = useState(() => prepared?.caseDescription ?? "");
   const [caseDescriptionLocked, setCaseDescriptionLocked] = useState(() => hasPayloadAnalysis(prepared));

   async function yieldExportPaint(): Promise<void> {
      await new Promise<void>((resolve) => {
         requestAnimationFrame(() => resolve());
      });
   }

   useEffect(() => {
      if (!initialPayload) return;
      setPrepared(initialPayload);
      setValidatedKeys(new Set());
      setCaseDescription(initialPayload.caseDescription ?? "");
      setCaseDescriptionLocked(hasPayloadAnalysis(initialPayload));
   }, [initialPayload]);

   useEffect(() => {
      if (!prepared) return;
      saveServitutRedegoerelseSessionCache(prepared, validatedKeys);
   }, [prepared, validatedKeys]);

   async function startAnalysis() {
      if (!prepared) return;
      if (!prepared.metadata.length) {
         message.error("Der er ingen klargjorte dokumenter til AI-analyse i denne visning.");
         return;
      }
      const payloadForAnalysis = applyCaseDescriptionToPayload(prepared, caseDescription);
      setPrepared(payloadForAnalysis);
      setCaseDescriptionLocked(true);
      await analyzePayload(payloadForAnalysis);
   }

   async function analyzePayload(payload: ServitutRedegoerelsePayload) {
      if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
         message.error(Localization.getText("No organization selected"));
         return;
      }
      setAnalyzing(true);
      try {
         const analyzer = createANNLLMAnalyzer({
            userRef: sessionState.userRef,
            userName: sessionState.userName,
            organizationId: sessionState.customerRef,
         });
         const metadataBatches = groupMetadataByAkt(payload.metadata);
         let failedBatches = 0;
         await runWithConcurrency(metadataBatches, SERVITUT_ANALYSIS_CONCURRENCY, async (metadataBatch) => {
            try {
               const result = await analyzer.analyzePdfUrlBatch(metadataBatch);
               setPrepared((currentPayload) => {
                  const updatedPayload = attachAnalysisResult(currentPayload ?? payload, result);
                  console.log("[Servitutredegørelse] AI analysis partial result", updatedPayload);
                  return updatedPayload;
               });
            } catch (error) {
               failedBatches += 1;
               console.error("[Servitutredegørelse] AI akt batch failed", metadataBatch, error);
            }
         });
         if (failedBatches) {
            message.error(`${failedBatches} akt-batches kunne ikke analyseres.`);
         }
      } catch (error) {
         console.error("[Servitutredegørelse] AI analysis failed", error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to analyze PDF"));
      } finally {
         setAnalyzing(false);
      }
   }

   async function openValidationViewer(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) {
      const handling = row.handling ?? "legacyLlmPdf";
      if (handling === "electronicZipPackage") {
         const srv = row.electronicServitut;
         const target = srv ? getTingbogDownloadTarget(srv) : undefined;
         if (!srv || !target) {
            message.error(Localization.getText("No PDF available for this document"));
            return;
         }
         setOpeningKey(row.key);
         try {
            const pdfBlob = await fetchTingbogDocumentBlob(target);
            const pdfUrl = URL.createObjectURL(pdfBlob);
            windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, {
               pdfUrl,
               pdfBlob,
               analysisResult: undefined,
               aktId: srv.aktId || srv.aktAliasId || row.analysisAktId,
               documentId: srv.dokumentId || srv.aktAliasId || row.key,
               contextType: AnnotationContextType.Servitut,
               organizationId: sessionState?.customerRef,
               userId: sessionState?.userRef,
               userName: sessionState?.userName,
               documentFilename: row.exportFileName || `${row.displayAktId}.pdf`,
            }));
         } catch (error) {
            message.error(error instanceof Error ? error.message : Localization.getText("Failed to open scanned akt PDF"));
         } finally {
            setOpeningKey(null);
         }
         return;
      }

      if (!row.aktId) {
         message.error(Localization.getText("No PDF available for this document"));
         return;
      }
      setOpeningKey(row.key);
      try {
         const analyzer = getANNLLMAnalyzer();
         const pdfBlob = await analyzer.fetchPdf(row.aktId);
         const pdfUrl = URL.createObjectURL(pdfBlob);
         windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, {
            pdfUrl,
            pdfBlob,
            analysisResult: row.analysisItem ? {
               items: [row.analysisItem],
               total_processed: 1,
               total_requested: 1,
               timestamp: new Date().toISOString(),
            } : undefined,
            aktId: row.aktId,
            documentId: row.key,
            contextType: AnnotationContextType.Servitut,
            organizationId: sessionState?.customerRef,
            userId: sessionState?.userRef,
            userName: sessionState?.userName,
            documentFilename: row.aktDocument,
            initialSelectedPages: row.selectedPages,
            onCloseSelectedPages: (pages: number[]) => updateRowSelectedPages(row.key, pages),
         }));
      } catch (error) {
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to open scanned akt PDF"));
      } finally {
         setOpeningKey(null);
      }
   }

   function updateRowSelectedPages(key: string, pages: number[]) {
      setPrepared((currentPayload) => {
         if (!currentPayload) return currentPayload;
         return {
            ...currentPayload,
            matrix: {
               ...currentPayload.matrix,
               rows: currentPayload.matrix.rows.map((row) => (
                  row.key === key ? { ...row, selectedPages: normalizePages(pages) } : row
               )),
            },
         };
      });
   }

   function toggleValidated(key: string) {
      setValidatedKeys((prev) => {
         const next = new Set(prev);
         if (next.has(key)) {
            next.delete(key);
         } else {
            next.add(key);
         }
         return next;
      });
   }

   async function exportBehandlede() {
      if (!prepared) return;
      const rows = prepared.matrix.rows.filter((row) => validatedKeys.has(row.key));
      if (!rows.length) {
         message.error("Der er ingen behandlede servitutter at eksportere.");
         return;
      }

      const totalSteps = 2 + rows.length;
      setExporting(true);
      setExportProgress({
         completedSteps: 0,
         totalSteps,
         stage: "spreadsheet",
         activeRowKey: null,
      });
      await yieldExportPaint();
      try {
         const zipBlob = await buildServitutRedegoerelseZip(prepared, validatedKeys, async (prog) => {
            setExportProgress(prog);
            await yieldExportPaint();
         });
         ExportData.downloadBlob(`Servitutredegørelse_${Date.now()}.zip`, zipBlob);
         message.info(`${rows.length} behandlede servitutter er eksporteret.`);
      } catch (error) {
         console.error("[Servitutredegørelse] export failed", error);
         message.error(error instanceof Error ? error.message : Localization.getText("Export failed"));
      } finally {
         setExporting(false);
         setExportProgress(null);
      }
   }

   return (
      <AdvancedDragModal
         window={props.managedWindow}
         title="Servitutredegørelse"
         onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
         allowFullscreen={true}
         allowUserMiniMize={true}
         PosDefault={{ top: "80px", left: "80px", width: "1100px", height: "720px" }}
      >
         <ADModalBody>
            {prepared ? (
               <ServitutMatrix
                  payload={prepared}
                  analyzing={analyzing}
                  openingKey={openingKey}
                  exporting={exporting}
                  exportProgress={exportProgress}
                  validatedKeys={validatedKeys}
                  onOpenRow={openValidationViewer}
                  onToggleValidated={toggleValidated}
                  onSetTreatedRows={(keys, treated) => {
                     setValidatedKeys((prev) => {
                        const next = new Set(prev);
                        keys.forEach((key) => {
                           if (treated) {
                              next.add(key);
                           } else {
                              next.delete(key);
                           }
                        });
                        return next;
                     });
                  }}
                  onAnalyze={startAnalysis}
                  canAnalyze={prepared.metadata.length > 0}
                  caseDescription={caseDescription}
                  caseDescriptionLocked={caseDescriptionLocked || analyzing}
                  onCaseDescriptionChange={setCaseDescription}
                  onExport={exportBehandlede}
               />
            ) : <Spinner />}
         </ADModalBody>
      </AdvancedDragModal>
   );
}

function servitutExportProgressPercent(p: ServitutRedegoerelseExportProgress): number {
   if (!p.totalSteps) {
      return 0;
   }
   return Math.min(100, Math.round((p.completedSteps / p.totalSteps) * 100));
}

function servitutExportProgressCaption(p: ServitutRedegoerelseExportProgress): string {
   if (p.stage === "spreadsheet" && p.completedSteps === 0) {
      return "Forbereder ZIP-eksport…";
   }
   if (p.stage === "spreadsheet") {
      return "Tilføjer Excel til arkivet…";
   }
   if (p.stage === "compress" && p.completedSteps < p.totalSteps) {
      return "Komprimerer ZIP…";
   }
   if (p.stage === "compress") {
      return "Afslutter…";
   }
   if (p.documentIndex !== undefined && p.documentTotal !== undefined) {
      return `Henter og tilføjer dokument ${p.documentIndex} af ${p.documentTotal}…`;
   }
   return "Eksporterer…";
}

function ServitutMatrix(props: {
   payload: ServitutRedegoerelsePayload;
   analyzing: boolean;
   openingKey: string | null;
   exporting: boolean;
   exportProgress: ServitutRedegoerelseExportProgress | null;
   validatedKeys: Set<string>;
   onOpenRow: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onToggleValidated: (key: string) => void;
   onSetTreatedRows: (keys: string[], treated: boolean) => void;
   onAnalyze: () => void;
   canAnalyze: boolean;
   caseDescription: string;
   caseDescriptionLocked: boolean;
   onCaseDescriptionChange: (value: string) => void;
   onExport: () => void;
}) {
   const [query, setQuery] = useState("");
   const [statusFilter, setStatusFilter] = useState<"all" | "found" | "missing" | "treated" | "untreated">("all");
   const [datoSort, setDatoSort] = useState<"default" | "asc" | "desc">("default");
   const [confidenceSort, setConfidenceSort] = useState<"default" | "asc" | "desc">("default");
   const normalizedQuery = query.trim().toLowerCase();
   const filteredRows = useMemo(() => {
      const rows = props.payload.matrix.rows.filter((row) => {
         const handling = row.handling ?? "legacyLlmPdf";
         const analysisSearchText = [
            row.analysisItem?.servitut_type,
            row.analysisItem?.paataleberettiget,
            row.analysisItem?.case_relevance_reasoning,
            row.analysisItem?.overall_confidence_reasoning,
            ...(row.analysisItem?.review_reasons || []),
            formatCaseRelevant(row.analysisItem?.case_relevant),
         ].filter(Boolean).join(" ").toLowerCase();
         const matchesQuery = !normalizedQuery ||
            row.datoLoebenr.toLowerCase().includes(normalizedQuery) ||
            row.displayAktId.toLowerCase().includes(normalizedQuery) ||
            row.summary.toLowerCase().includes(normalizedQuery) ||
            analysisSearchText.includes(normalizedQuery) ||
            row.includedBfes.some((bfe) => `${bfe}`.includes(normalizedQuery));
         if (!matchesQuery) return false;
         if (statusFilter === "found") {
            return Boolean(row.analysisItem?.pages_kept?.length)
               || (handling === "electronicZipPackage" && row.pdfStatus === "ready");
         }
         if (statusFilter === "missing") {
            return handling === "legacyLlmPdf"
               && Boolean(row.analysisItem)
               && !(row.analysisItem?.pages_kept?.length);
         }
         if (statusFilter === "treated") return props.validatedKeys.has(row.key);
         if (statusFilter === "untreated") return !props.validatedKeys.has(row.key);
         return true;
      });
      const defaultSortedRows = sortServitutRows(rows, props.payload.matrix.columns, datoSort);
      if (confidenceSort === "default") {
         return defaultSortedRows;
      }
      return sortRowsByReviewConfidence(
         sortServitutRows(rows, props.payload.matrix.columns, "default"),
         confidenceSort,
      );
   }, [confidenceSort, datoSort, normalizedQuery, props.payload.matrix.columns, props.payload.matrix.rows, props.validatedKeys, statusFilter]);
   const analyzedCount = props.payload.matrix.rows.filter((row) => Boolean(row.analysisItem)).length;
   const foundCount = props.payload.matrix.rows.filter((row) => Boolean(row.analysisItem?.pages_kept?.length)
      || ((row.handling ?? "legacyLlmPdf") === "electronicZipPackage" && row.pdfStatus === "ready")).length;
   const highConfidenceCount = props.payload.matrix.rows.filter((row) => getReviewTriage(row).level === "good").length;
   const needsReviewCount = props.payload.matrix.rows.filter((row) => {
      const triage = getReviewTriage(row);
      return triage.level === "bad" || row.analysisItem?.needs_review === true;
   }).length;
   const treatedCount = props.validatedKeys.size;
   const filteredRowKeys = filteredRows.map((row) => row.key);
   const allFilteredRowsTreated = filteredRowKeys.length > 0 && filteredRowKeys.every((key) => props.validatedKeys.has(key));
   const cycleDatoSort = () => {
      setConfidenceSort("default");
      setDatoSort((current) => (
         current === "default" ? "asc" : current === "asc" ? "desc" : "default"
      ));
   };
   const cycleConfidenceSort = () => {
      setDatoSort("default");
      setConfidenceSort((current) => (
         current === "default" ? "desc" : current === "desc" ? "asc" : "default"
      ));
   };

   const exportProgressPct = props.exportProgress
      ? servitutExportProgressPercent(props.exportProgress)
      : 0;
   const exportProgressLabel = props.exportProgress
      ? servitutExportProgressCaption(props.exportProgress)
      : "";
   const hasCaseDescription = Boolean(props.payload.caseDescription?.trim());

   return (
      <div className="servitut-redegoerelse">
         <div className="servitut-redegoerelse__hero">
            <div>
               <div className="servitut-redegoerelse__eyebrow">Servitutredegørelse</div>
               <h2>Gennemgå servitutter</h2>
               <p>Gennemgå servitutmatrixen først, og start AI-analysen når du er klar. Brug sikkerhedsfarven til hurtigt at prioritere ekspertgennemgangen.</p>
            </div>
            <div className="servitut-redegoerelse__aiPanel">
               <div className={`servitut-redegoerelse__runState ${props.analyzing ? "servitut-redegoerelse__runState--active" : ""}`}>
                  <div className="servitut-redegoerelse__runStatus">
                     {props.analyzing ? <Spinner size="sm" /> : <span className="servitut-redegoerelse__dot" />}
                     <span>{props.analyzing ? Localization.getText("Analyzing with AI...") : analyzedCount ? "Analyse klar" : "Afventer AI-analyse"}</span>
                  </div>
                  <button
                     className="servitut-redegoerelse__aiStart"
                     disabled={props.analyzing || props.exporting || !props.canAnalyze}
                     onClick={props.onAnalyze}
                  >
                     Start AI-analyse
                  </button>
               </div>
               <label className="servitut-redegoerelse__casePanel">
                  <textarea
                     className="servitut-redegoerelse__caseInput"
                     value={props.caseDescription}
                     disabled={props.caseDescriptionLocked}
                     onChange={(e) => props.onCaseDescriptionChange(e.currentTarget.value)}
                     placeholder="Valgfrit: beskriv den konkrete problemstilling, som AI skal vurdere relevansen af."
                     rows={3}
                  />
               </label>
            </div>
         </div>

         <div className="servitut-redegoerelse__stats">
            <MetricCard label="Servitutter" value={props.payload.matrix.rows.length} />
            <MetricCard label="AI behandlet" value={`${analyzedCount}/${props.payload.matrix.rows.length}`} />
            <MetricCard label="Dokumenter fundet" value={foundCount} tone="success" />
            <MetricCard label="Høj sikkerhed" value={highConfidenceCount} tone="success" />
            <MetricCard label="Kræver tjek" value={needsReviewCount} tone={needsReviewCount ? "error" : "success"} />
            <MetricCard label="Behandlet" value={`${treatedCount}/${props.payload.matrix.rows.length}`} tone="accent" />
         </div>

         <div className="servitut-redegoerelse__toolbar">
            <div className="servitut-redegoerelse__toolbarRow">
               <input
                  className="servitut-redegoerelse__search"
                  value={query}
                  onChange={(e) => setQuery(e.currentTarget.value)}
                  placeholder="Søg i akt, tekst eller BFE..."
               />
               <select
                  className="servitut-redegoerelse__filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.currentTarget.value as typeof statusFilter)}
               >
                  <option value="all">Alle rækker</option>
                  <option value="found">AI fandt sider</option>
                  <option value="missing">AI fandt ikke sider</option>
                  <option value="treated">Behandlet</option>
                  <option value="untreated">Ikke behandlet</option>
               </select>
               <button
                  className="servitut-redegoerelse__export"
                  disabled={props.exporting || props.validatedKeys.size === 0}
                  onClick={props.onExport}
               >
                  {props.exporting ? Localization.getText("Exporting...") : `Eksporter behandlede (${props.validatedKeys.size})`}
               </button>
            </div>
            {props.exporting && props.exportProgress ? (
               <div className="servitut-redegoerelse__exportProgress" aria-live="polite">
                  <div className="servitut-redegoerelse__exportProgressTrack">
                     <div
                        className="servitut-redegoerelse__exportProgressFill"
                        style={{ width: `${exportProgressPct}%` }}
                     />
                  </div>
                  <div className="servitut-redegoerelse__exportProgressFooter">
                     <Spinner animation="border" size="sm" className="servitut-redegoerelse__exportProgressSpinner" />
                     <span className="servitut-redegoerelse__exportProgressText">
                        {exportProgressPct}% — {exportProgressLabel}
                     </span>
                  </div>
               </div>
            ) : null}
         </div>

         <div className="servitut-redegoerelse__tableWrap">
            <table className="servitut-redegoerelse__table">
               <thead>
                  <tr>
                     <th className="servitut-redegoerelse__treatedHead">
                        <label className="servitut-redegoerelse__check servitut-redegoerelse__check--header">
                           <input
                              type="checkbox"
                              checked={allFilteredRowsTreated}
                              disabled={filteredRowKeys.length === 0 || props.exporting}
                              onChange={() => props.onSetTreatedRows(filteredRowKeys, !allFilteredRowsTreated)}
                           />
                           <span>Behandlet</span>
                        </label>
                     </th>
                     <th>
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={cycleDatoSort}
                           title="Sortér efter dato løbenr. Klik igen for stigende, faldende og standardsortering."
                        >
                           <span>Dato løbenr</span>
                           <span>{datoSort === "asc" ? "↑" : datoSort === "desc" ? "↓" : "↕"}</span>
                        </button>
                     </th>
                     <th>Prioritet</th>
                     <th>AI forslag</th>
                     <th>
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={cycleConfidenceSort}
                           title="Sortér efter AI sikkerhed. Klik igen for højeste først, laveste først og standardsortering."
                        >
                           <span>AI sikkerhed</span>
                           <span>{confidenceSort === "asc" ? "↑" : confidenceSort === "desc" ? "↓" : "↕"}</span>
                        </button>
                     </th>
                     <th>Gemmes</th>
                     <th>Servituttype</th>
                     <th>Påtaleberettiget</th>
                     <th>Case relevant</th>
                     {props.payload.matrix.columns.map((bfe) => {
                        const label = getServitutMatrixColumnLabel(props.payload, bfe);
                        const labelParts = getServitutMatrixColumnLabelParts(label);
                        const labelDimensions = getServitutMatrixColumnLabelDimensions(labelParts);
                        return (
                           <th
                              key={bfe}
                              className="servitut-redegoerelse__bfeHead"
                              title={`${label} (BFE ${bfe})`}
                              style={{
                                 "--servitut-bfe-head-width": labelDimensions.columnWidth,
                                 "--servitut-bfe-label-width": labelDimensions.labelWidth,
                                 "--servitut-bfe-head-height": labelDimensions.headerHeight,
                              } as ServitutBfeHeaderStyle}
                           >
                              <span className="servitut-redegoerelse__bfeHeadInner">
                                 <span className="servitut-redegoerelse__bfeHeadMatrikel">{labelParts.matrikel}</span>
                                 {labelParts.ejerlav ? (
                                    <span className="servitut-redegoerelse__bfeHeadEjerlav">{labelParts.ejerlav}</span>
                                 ) : null}
                              </span>
                           </th>
                        );
                     })}
                     <th>Servitut</th>
                  </tr>
               </thead>
               <tbody>
                  {filteredRows.map((row) => {
                     const rowHandlingVal = row.handling ?? "legacyLlmPdf";
                     const hasPages = Boolean(row.analysisItem?.pages_kept?.length);
                     const hasSelectedPages = Boolean(row.selectedPages?.length);
                     const isTreated = props.validatedKeys.has(row.key);
                     const aiSuggestionChanged = rowHandlingVal === "legacyLlmPdf"
                        && Boolean(row.analysisItem && !arePageSelectionsEqual(row.analysisItem.pages_kept || [], row.selectedPages || []));
                     const rowOpensViewer = (row.handling ?? "legacyLlmPdf") === "electronicZipPackage"
                        ? Boolean(row.electronicServitut)
                        : row.pdfStatus === "ready";
                     const rowIsExportFocus = Boolean(
                        props.exporting
                        && props.exportProgress?.stage === "document"
                        && props.exportProgress.activeRowKey === row.key,
                     );
                     const caseRelevant = getCaseRelevantDisplay(row.analysisItem?.case_relevant, hasCaseDescription);
                     const reviewTriage = getReviewTriage(row);
                     return (
                        <tr
                           key={row.key}
                           className={`servitut-redegoerelse__row servitut-redegoerelse__row--review-${reviewTriage.level} ${rowOpensViewer ? "servitut-redegoerelse__row--clickable" : ""} ${isTreated ? "servitut-redegoerelse__row--validated" : ""}${rowIsExportFocus ? " servitut-redegoerelse__row--exportActive" : ""}`}
                           onClick={() => {
                              if (rowOpensViewer) {
                                 props.onOpenRow(row);
                              }
                           }}
                        >
                           <td className="servitut-redegoerelse__treatedCell" onClick={(e) => e.stopPropagation()}>
                              <label className="servitut-redegoerelse__check servitut-redegoerelse__check--withSpinner" title={isTreated ? "Behandlet" : "Mangler behandling"} aria-label={isTreated ? "Behandlet" : "Mangler behandling"}>
                                 <input
                                    type="checkbox"
                                    checked={isTreated}
                                    disabled={props.exporting}
                                    onChange={() => props.onToggleValidated(row.key)}
                                 />
                                 {rowIsExportFocus ? (
                                    <Spinner animation="border" role="status" size="sm" className="servitut-redegoerelse__rowExportSpinner" />
                                 ) : null}
                              </label>
                           </td>
                           <td className="servitut-redegoerelse__aktCell">
                              <div className="servitut-redegoerelse__aktIdLine">
                                 <div className="servitut-redegoerelse__aktId">{row.datoLoebenr || row.displayAktId}</div>
                              </div>
                              <div className="servitut-redegoerelse__muted">
                                 <span>{row.aktDocument || row.pdfStatus}</span>
                                 {row.pdfSource === "ovrigAkt" ? (
                                    <span
                                       className="servitut-redegoerelse__sourceHint servitut-redegoerelse__sourceHint--ovrig"
                                       title={getAktSourceText(row.pdfSource)}
                                       aria-label={getAktSourceText(row.pdfSource)}
                                    >
                                       !
                                    </span>
                                 ) : null}
                              </div>
                           </td>
                           <td><span className="servitut-redegoerelse__priority">{row.priority || "-"}</span></td>
                           <td>
                              {props.openingKey === row.key ? (
                                 <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--muted">{Localization.getText("Loading...")}</span>
                              ) : rowHandlingVal === "electronicZipPackage" ? (
                                 row.analysisItem ? (
                                    <div className="servitut-redegoerelse__aiCell">
                                       <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--success">
                                          Analyseret
                                       </span>
                                       <span className="servitut-redegoerelse__confidence">
                                          Sikkerhed {formatConfidence(row.analysisItem.confidence)}
                                       </span>
                                    </div>
                                 ) : props.analyzing && row.pdfStatus === "ready" ? (
                                    <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--pending">
                                       <Spinner size="sm" />
                                       <span>AI analyserer...</span>
                                    </span>
                                 ) : row.pdfStatus === "ready" ? (
                                    <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--muted">Ingen analyse</span>
                                 ) : (
                                    <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--muted">Ikke tilgængelig til download</span>
                                 )
                              ) : row.analysisItem ? (
                                 <div className={`servitut-redegoerelse__aiCell ${aiSuggestionChanged ? "servitut-redegoerelse__aiCell--overridden" : ""}`}>
                                    <span className={`servitut-redegoerelse__badge ${hasPages ? "servitut-redegoerelse__badge--success" : "servitut-redegoerelse__badge--error"}`}>
                                       {hasPages ? `Side ${formatPageRanges(row.analysisItem.pages_kept)}` : "Ikke fundet"}
                                    </span>
                                    <span className="servitut-redegoerelse__confidence">Sikkerhed {formatConfidence(row.analysisItem.confidence)}</span>
                                 </div>
                              ) : props.analyzing && rowHandlingVal === "legacyLlmPdf" ? (
                                 <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--pending">
                                    <Spinner size="sm" />
                                    <span>AI analyserer...</span>
                                 </span>
                              ) : (
                                 <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--muted">Ingen analyse</span>
                              )}
                           </td>
                           <td className="servitut-redegoerelse__confidenceCell">
                              <div className="servitut-redegoerelse__aiCell" title={reviewTriage.title}>
                                 <span className={`servitut-redegoerelse__reviewBadge servitut-redegoerelse__reviewBadge--${reviewTriage.level}`}>
                                    <span className="servitut-redegoerelse__reviewDot" />
                                    <span>{reviewTriage.label}</span>
                                 </span>
                                 {reviewTriage.detail ? (
                                    <span className="servitut-redegoerelse__confidence">{reviewTriage.detail}</span>
                                 ) : null}
                              </div>
                           </td>
                           <td>
                              {rowHandlingVal === "electronicZipPackage" ? (
                                 row.pdfStatus === "ready" ? (
                                    <div className="servitut-redegoerelse__documentPackageBadges">
                                       <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--success">
                                          Fuldt dokument
                                       </span>
                                       <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--success">
                                          SERVIMAP
                                       </span>
                                       <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--success">
                                          Bilag
                                       </span>
                                    </div>
                                 ) : (
                                    <span className="servitut-redegoerelse__badge servitut-redegoerelse__badge--warning">—</span>
                                 )
                              ) : (
                                 <span className={`servitut-redegoerelse__badge ${hasSelectedPages ? "servitut-redegoerelse__badge--success" : "servitut-redegoerelse__badge--warning"} ${aiSuggestionChanged ? "servitut-redegoerelse__badge--manual" : ""}`}>
                                    {hasSelectedPages ? `Side ${formatPageRanges(row.selectedPages || [])}` : "Ingen sider"}
                                 </span>
                              )}
                           </td>
                           <td className="servitut-redegoerelse__detailCell">
                              {row.analysisItem?.servitut_type ? (
                                 <span className="servitut-redegoerelse__badge">
                                    {formatServitutType(row.analysisItem.servitut_type)}
                                 </span>
                              ) : (
                                 <span className="servitut-redegoerelse__mutedText">-</span>
                              )}
                           </td>
                           <td className="servitut-redegoerelse__detailCell">
                              <span title={row.analysisItem?.paataleberettiget || undefined}>
                                 {row.analysisItem?.paataleberettiget || "-"}
                              </span>
                           </td>
                           <td className="servitut-redegoerelse__detailCell">
                              <div className="servitut-redegoerelse__aiCell" title={row.analysisItem?.case_relevance_reasoning || undefined}>
                                 <span className={`servitut-redegoerelse__badge ${caseRelevant.badgeClass}`}>
                                    {caseRelevant.label}
                                 </span>
                                 {row.analysisItem?.case_relevance_reasoning ? (
                                    <span className="servitut-redegoerelse__confidence">{row.analysisItem.case_relevance_reasoning}</span>
                                 ) : null}
                              </div>
                           </td>
                           {props.payload.matrix.columns.map((bfe) => (
                              <td key={`${row.key}-${bfe}`} className="servitut-redegoerelse__bfeCell">
                                 {row.includedBfes.includes(bfe) ? <span className="servitut-redegoerelse__included">✓</span> : ""}
                              </td>
                           ))}
                           <td className="servitut-redegoerelse__summaryCell">
                              <div className="servitut-redegoerelse__summary">{row.summary || "Ingen tekst"}</div>
                              {row.analysisItem?.summary ? (
                                 <div className="servitut-redegoerelse__aiSummary">
                                    <span className="servitut-redegoerelse__aiIcon">AI</span>
                                    <span>{row.analysisItem.summary}</span>
                                 </div>
                              ) : null}
                           </td>
                        </tr>
                     );
                  })}
               </tbody>
            </table>
            {filteredRows.length === 0 ? <div className="servitut-redegoerelse__empty">Ingen rækker matcher filtreringen.</div> : null}
         </div>
      </div>
   );
}

function MetricCard(props: { label: string; value: string | number; tone?: "success" | "accent" | "error"; title?: string; compactText?: boolean }) {
   return (
      <div
         className={`servitut-redegoerelse__metric ${props.tone ? `servitut-redegoerelse__metric--${props.tone}` : ""}`}
         title={props.title}
      >
         <div className={`servitut-redegoerelse__metricValue ${props.compactText ? "servitut-redegoerelse__metricValue--compactText" : ""}`}>
            {props.value}
         </div>
         <div className="servitut-redegoerelse__metricLabel">{props.label}</div>
      </div>
   );
}

function buildServitutMatrixColumnLabels(
   data: ServitutRapportBfeData[],
   colorList: SFEColorEntry[]
): Record<string, string> {
   const dataByBfe = new Map(data.map((entry) => [Number(entry.BFEnummer), entry]));
   return Object.fromEntries(
      colorList.map((entry) => [
         entry.sfe,
         formatServitutMatrixPropertyColumn(dataByBfe.get(entry.sfe)) || `${entry.sfe}`,
      ])
   );
}

function formatServitutMatrixPropertyColumn(bfeData?: ServitutRapportBfeData): string {
   const sfe = bfeData?.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.find((node: any) => node.status === "Gældende")
      || bfeData?.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0];
   const jordstykker = (sfe?.jordstykkeSamlesISamletFastEjendom?.nodes || [])
      .filter((jordstykke: any) => jordstykke.status === "Gældende")
      .filter((jordstykke: any) => !sfe?.id_lokalId || jordstykke.samletFastEjendomLokalId === sfe.id_lokalId)
      .sort((a: any, b: any) => {
         const ejerlavDiff = parseInt(a.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10)
            - parseInt(b.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10);
         if (ejerlavDiff !== 0) {
            return ejerlavDiff;
         }
         return PropertyInformation.matrikelSortFunc(a.matrikelnummer || "", b.matrikelnummer || "");
      });

   const firstJordstykke = jordstykker[0];
   if (!firstJordstykke?.matrikelnummer) {
      return "";
   }
   const ejerlavsnavn = firstJordstykke.jordstykkeLiggerIEjerlav?.ejerlavsnavn;
   return [
      `${firstJordstykke.matrikelnummer}${jordstykker.length > 1 ? " m.fl." : ""}`,
      ejerlavsnavn,
   ].filter(Boolean).join(", ");
}

function getServitutMatrixColumnLabel(payload: ServitutRedegoerelsePayload, bfe: number): string {
   return payload.matrix.columnLabels?.[bfe] || `${bfe}`;
}

function getServitutMatrixColumnLabelParts(label: string): { matrikel: string; ejerlav?: string } {
   const [matrikel, ...ejerlavParts] = label.split(", ");
   return {
      matrikel: matrikel || label,
      ejerlav: ejerlavParts.join(", ") || undefined,
   };
}

function getServitutMatrixColumnLabelDimensions(labelParts: { matrikel: string; ejerlav?: string }): {
   columnWidth: string;
   labelWidth: string;
   headerHeight: string;
} {
   const longestLineLength = Math.max(labelParts.matrikel.length, labelParts.ejerlav?.length || 0);
   const labelWidthPx = clampNumber(longestLineLength * 7.2, 135, 260);
   return {
      labelWidth: `${labelWidthPx}px`,
      columnWidth: `${clampNumber(labelWidthPx * 0.42, 54, 112)}px`,
      headerHeight: `${clampNumber(labelWidthPx * 0.98, 145, 265)}px`,
   };
}

function clampNumber(value: number, min: number, max: number): number {
   return Math.max(min, Math.min(max, value));
}

function hasPayloadAnalysis(payload: ServitutRedegoerelsePayload | null): boolean {
   return Boolean(payload?.matrix.rows.some((row) => row.analysisItem));
}

function applyCaseDescriptionToPayload(
   payload: ServitutRedegoerelsePayload,
   caseDescription: string
): ServitutRedegoerelsePayload {
   const normalizedCaseDescription = caseDescription.trim();
   return {
      ...payload,
      caseDescription: normalizedCaseDescription || undefined,
      metadata: payload.metadata.map((metadata) => {
         if (!normalizedCaseDescription) {
            const rest = { ...metadata };
            delete rest.caseDescription;
            return rest;
         }
         return {
            ...metadata,
            caseDescription: normalizedCaseDescription,
         };
      }),
   };
}

async function prepareServitutRedegoerelsePayload(
   data: ServitutRapportBfeData[],
   colorList: SFEColorEntry[]
): Promise<ServitutRedegoerelsePayload> {
   const rows = buildServitutMatrixRows(data);
   const filesByDocument = new Map<string, ServitutRedegoerelsePreparedAkt>();
   const metadata: LLMMetadata[] = [];
   const analyzer = getANNLLMAnalyzer();

   for (const row of rows) {
      const firstBfe = data.find((bfeData) => row.includedBfes.has(Number(bfeData.BFEnummer)));
      if (row.handling === "electronicZipPackage") {
         if (!isEligibleDigitalServitut(row.servitut)) {
            row.pdfStatus = "missing";
         } else {
            row.pdfStatus = "ready";
            row.aktDocument = "Digital tinglysnings-PDF";
            const xmlText = await fetchDigitalServitutXml(row.servitut).catch((error) => {
               console.error("[Servitutredegørelse] failed to fetch digital servitut XML", row.key, error);
               return null;
            });
            if (xmlText) {
               const entryMetadata: LLMMetadata = {
                  id: row.key,
                  document: xmlText,
                  documentUrl: null,
                  aktId: null,
                  tekster: getServitutTekster(row.servitut),
                  fromOvrigDataSource: null,
               };
               metadata.push(entryMetadata);
            }
         }
         continue;
      }

      const aktTarget = resolveAktTarget(row.servitut, firstBfe);
      if (!aktTarget) {
         row.pdfStatus = "missing";
         continue;
      }

      try {
         const document = getAktDocumentName(aktTarget.aktId);
         let file = filesByDocument.get(document);
         if (!file) {
            file = {
               document,
               aktId: aktTarget.aktId,
               documentUrl: await analyzer.getAktFileUrl(aktTarget.aktId),
               source: aktTarget.source,
               servitutKeys: [],
            };
            filesByDocument.set(document, file);
         }
         const entryMetadata: LLMMetadata = {
            id: row.key,
            document,
            documentUrl: file.documentUrl,
            aktId: row.servitut?.aktId || row.servitut?.aktAliasId || row.key,
            tekster: getServitutTekster(row.servitut),
            fromOvrigDataSource: aktTarget.fromOvrigDataSource,
         };
         file.servitutKeys.push(row.key);
         metadata.push(entryMetadata);
         row.pdfStatus = "ready";
         row.pdfSource = aktTarget.source;
         row.aktDocument = document;
         row.aktId = aktTarget.aktId;
         row.documentUrl = file.documentUrl;
      } catch (error) {
         row.pdfStatus = "failed";
         console.error("[Servitutredegørelse] failed to prepare akt", row.key, error);
      }
   }

   const electronicReadyRows = rows.filter((r) => r.handling === "electronicZipPackage" && r.pdfStatus === "ready").length;

   return {
      metadata,
      matrix: {
         columns: colorList.map((entry) => entry.sfe),
         columnLabels: buildServitutMatrixColumnLabels(data, colorList),
         uniqueAktCount: filesByDocument.size + electronicReadyRows,
         rows: rows.map((row) => ({
            key: row.key,
            handling: row.handling,
            electronicServitut:
               row.handling === "electronicZipPackage" && row.pdfStatus === "ready"
                  ? ({ ...row.servitut } as ServitutElectronicPackageServitut)
                  : undefined,
            displayAktId: row.displayAktId,
            datoLoebenr: row.datoLoebenr,
            priority: row.priority,
            summary: row.summary,
            analysisAktId: row.analysisAktId,
            includedBfes: Array.from(row.includedBfes),
            pdfStatus: row.pdfStatus,
            pdfSource: row.pdfSource,
            aktDocument: row.aktDocument,
            aktId: row.aktId,
            documentUrl: row.documentUrl,
            analysisItem: row.analysisItem,
            selectedPages: row.selectedPages,
            exportFileName: row.exportFileName,
         })),
      },
   };
}

function buildServitutMatrixRows(data: ServitutRapportBfeData[]): ServitutMatrixRow[] {
   const rowsByKey = new Map<string, ServitutMatrixRow>();
   data.forEach((bfeData) => {
      const bfe = Number(bfeData.BFEnummer);
      (bfeData.servitutter || []).forEach((servitut) => {
         const handling: ServitutRedegoerelseRowHandling = isTingbogElectronicDocument(servitut)
            ? "electronicZipPackage"
            : "legacyLlmPdf";
         const key = getServitutKey(servitut);
         const existing = rowsByKey.get(key);
         if (existing) {
            existing.includedBfes.add(bfe);
            return;
         }
         rowsByKey.set(key, {
            key,
            servitut,
            handling,
            displayAktId: aktIdFormat(servitut.aktId || servitut.aktAliasId || key),
            datoLoebenr: getServitutDatoLoebenr(servitut, key),
            priority: `${servitut.prioritetNummer || ""}`,
            summary: getServitutSummary(servitut),
            analysisAktId: servitut?.aktId || servitut?.aktAliasId || key,
            includedBfes: new Set([bfe]),
            pdfStatus: "pending",
            exportFileName: getServitutMatrixFileName(servitut),
         });
      });
   });
   return Array.from(rowsByKey.values()).sort((a, b) => {
      const bfeDiff = Math.min(...Array.from(a.includedBfes)) - Math.min(...Array.from(b.includedBfes));
      if (bfeDiff !== 0) return bfeDiff;
      return comparePriorityThenDato(a, b);
   });
}

function getServitutKey(servitut: any): string {
   const identity = normalizeServitutIdentity(servitut);
   return identity.documentId || identity.aktAliasId || identity.aktId || servitut?.dokumentFilnavn || getServitutSummary(servitut);
}

function getServitutSummary(servitut: any): string {
   return (servitut?.tekstSummarisk || (Array.isArray(servitut?.tekster) ? servitut.tekster.map((t: any) => t?.tekst ?? t).join(", ") : "") || "").trim();
}

function getServitutDatoLoebenr(servitut: any, fallback: string): string {
   const identity = normalizeServitutIdentity(servitut);
   return identity.datoloebenr || aktIdFormat(identity.aktAliasId || identity.aktId || servitut?.dokumentFilnavn || fallback);
}

function getServitutMatrixFileName(servitut: any): string {
   const identity = normalizeServitutIdentity(servitut);
   return getServitutDownloadFileName({
      datoloebenr: identity.datoloebenr,
      priorityNumber: servitut?.prioritetNummer,
      headingText: getServitutSummary(servitut),
      fallbackStem: identity.aktAliasId || identity.aktId || servitut?.dokumentFilnavn || "servitut",
   });
}

function getServitutTekster(servitut: any): string[] {
   const tekster: string[] = [];
   if (Array.isArray(servitut?.tekster)) {
      tekster.push(...servitut.tekster.map((t: any) => t?.tekst ?? t).filter(Boolean));
   }
   if (servitut?.tekstSummarisk) {
      tekster.push(servitut.tekstSummarisk);
   }
   return tekster;
}

async function fetchDigitalServitutXml(servitut: any): Promise<string | null> {
   const dokumentId = servitut?.dokumentId?.trim();
   if (!dokumentId) {
      return null;
   }
   const xmlDoc = await DC.TL.DokumentTingbog(dokumentId);
   return new XMLSerializer().serializeToString(xmlDoc);
}

function resolveAktTarget(
   servitut: any,
   bfeData?: ServitutRapportBfeData
): { aktId: string; source: ServitutRedegoerelsePreparedAkt["source"]; fromOvrigDataSource: boolean } | null {
   const dokumentFilnavn = servitut?.dokumentFilnavn?.trim();
   if (dokumentFilnavn) {
      return { aktId: dokumentFilnavn, source: "servitutAkt", fromOvrigDataSource: false };
   }

   const fallbackAkt = bfeData?.stamOplysninger?.indskannetAktDokumentId?.trim();
   if (fallbackAkt) {
      return { aktId: fallbackAkt, source: "ovrigAkt", fromOvrigDataSource: true };
   }

   return null;
}

function getAktDocumentName(aktId: string): string {
   return `${aktId.replace(/\.pdf$/i, "")}.pdf`;
}

function formatConfidence(confidence?: number): string {
   if (confidence === undefined || confidence === null) return "-";
   const numeric = normalizeConfidenceValue(confidence);
   if (numeric === null) return "-";
   return `${Math.round(numeric * 100)}%`;
}

function formatServitutType(value?: string): string {
   if (!value) return "-";
   const normalized = value.toLowerCase().replace(/\s+/g, "");
   if (normalized === "rådighedsservitut") return "Rådighed";
   if (normalized === "tilstandsservitut") return "Tilstand";
   return value;
}

function formatCaseRelevant(value?: boolean): string {
   if (value === true) return "Ja";
   if (value === false) return "Nej";
   return "";
}

function getCaseRelevantDisplay(value: boolean | undefined, hasCaseDescription: boolean): { label: string; badgeClass: string } {
   if (!hasCaseDescription) {
      return { label: "Ingen case", badgeClass: "servitut-redegoerelse__badge--muted" };
   }
   if (value === true) {
      return { label: "Ja", badgeClass: "servitut-redegoerelse__badge--success" };
   }
   if (value === false) {
      return { label: "Nej", badgeClass: "servitut-redegoerelse__badge--warning" };
   }
   return { label: "Ikke vurderet", badgeClass: "servitut-redegoerelse__badge--muted" };
}

type ReviewTriageLevel = "good" | "medium" | "bad" | "pending" | "unknown";

function normalizeConfidenceValue(value?: number): number | null {
   if (value === undefined || value === null) {
      return null;
   }
   if (!Number.isFinite(value)) {
      return null;
   }
   // Older cached/dev results used 1-10. Normalize defensively while backend now returns 0-1.
   return value > 1 ? value / 10 : value;
}

function getReviewTriage(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): {
   level: ReviewTriageLevel;
   label: string;
   detail: string;
   title: string;
} {
   const item = row.analysisItem;
   if (!item) {
      return {
         level: "pending",
         label: "Afventer",
         detail: "",
         title: "AI har endnu ikke returneret en samlet sikkerhed for rækken.",
      };
   }

   const confidence = normalizeConfidenceValue(item.overall_confidence ?? item.confidence);
   const percent = confidence === null ? "" : `${Math.round(confidence * 100)}%`;
   const reviewReasons = item.review_reasons?.filter(Boolean) || [];
   const detailParts = [
      item.overall_confidence_reasoning,
      ...reviewReasons,
   ].filter(Boolean);
   const detail = detailParts[0] || "";
   const title = detailParts.length ? detailParts.join("\n") : item.reasoning || "Ingen yderligere begrundelse.";

   if (item.needs_review === true) {
      return {
         level: "bad",
         label: percent ? `Tjek ${percent}` : "Kræver tjek",
         detail,
         title,
      };
   }
   if (confidence === null) {
      return {
         level: "unknown",
         label: "Ukendt",
         detail,
         title,
      };
   }
   if (confidence >= 0.85) {
      return {
         level: "good",
         label: `Høj ${percent}`,
         detail,
         title,
      };
   }
   if (confidence >= 0.6) {
      return {
         level: "medium",
         label: `Middel ${percent}`,
         detail,
         title,
      };
   }
   return {
      level: "bad",
      label: `Lav ${percent}`,
      detail,
      title,
   };
}

function formatPageRanges(pages: number[]): string {
   const sortedPages = normalizePages(pages);
   if (!sortedPages.length) return "-";

   const ranges: string[] = [];
   let rangeStart = sortedPages[0];
   let previousPage = sortedPages[0];
   for (let index = 1; index < sortedPages.length; index += 1) {
      const page = sortedPages[index];
      if (page === previousPage + 1) {
         previousPage = page;
         continue;
      }
      ranges.push(rangeStart === previousPage ? `${rangeStart}` : `${rangeStart}-${previousPage}`);
      rangeStart = page;
      previousPage = page;
   }
   ranges.push(rangeStart === previousPage ? `${rangeStart}` : `${rangeStart}-${previousPage}`);
   return ranges.join(", ");
}

function normalizePages(pages: number[]): number[] {
   return Array.from(new Set(pages))
      .filter((page) => Number.isFinite(page) && page > 0)
      .sort((a, b) => a - b);
}

function arePageSelectionsEqual(a: number[], b: number[]): boolean {
   const normalizedA = normalizePages(a);
   const normalizedB = normalizePages(b);
   return normalizedA.length === normalizedB.length && normalizedA.every((page, index) => page === normalizedB[index]);
}

function getAktSourceText(source?: ServitutRedegoerelsePreparedAkt["source"]): string {
   if (source === "ovrigAkt") return "Akt-id er fundet via Øvrige akter.";
   if (source === "servitutAkt") return "Akt-id kommer direkte fra servitutten.";
   return "Akt-kilde er ikke afklaret.";
}

function sortServitutRows<T extends { includedBfes: number[]; priority: string; datoLoebenr?: string; displayAktId: string }>(
   rows: T[],
   columns: number[],
   datoSort: "default" | "asc" | "desc"
): T[] {
   return [...rows].sort((a, b) => {
      if (datoSort !== "default") {
         const dateDiff = compareDatoLoebenr(a, b);
         return datoSort === "asc" ? dateDiff : -dateDiff;
      }

      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return comparePriorityThenDato(a, b);
   });
}

function sortRowsByReviewConfidence<T extends ServitutRedegoerelsePayload["matrix"]["rows"][number]>(
   rows: T[],
   direction: "asc" | "desc"
): T[] {
   return [...rows].sort((a, b) => {
      const diff = getReviewConfidenceSortValue(a) - getReviewConfidenceSortValue(b);
      return direction === "asc" ? diff : -diff;
   });
}

function getReviewConfidenceSortValue(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): number {
   const item = row.analysisItem;
   if (!item) {
      return -1;
   }
   const confidence = normalizeConfidenceValue(item.overall_confidence ?? item.confidence);
   if (item.needs_review === true) {
      return -0.5 + ((confidence ?? 0) / 100);
   }
   return confidence ?? -0.25;
}

function getFirstBfeColumnIndex(includedBfes: number[], columns: number[]): number {
   const columnIndexes = includedBfes.map((bfe) => {
      const index = columns.indexOf(bfe);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
   });
   return Math.min(...columnIndexes);
}

function comparePriorityThenDato(
   a: { priority: string; datoLoebenr?: string; displayAktId: string },
   b: { priority: string; datoLoebenr?: string; displayAktId: string }
): number {
   const priorityDiff = (parseInt(a.priority) || 0) - (parseInt(b.priority) || 0);
   if (priorityDiff !== 0) return priorityDiff;
   return compareDatoLoebenr(a, b);
}

function compareDatoLoebenr(
   a: { datoLoebenr?: string; displayAktId: string },
   b: { datoLoebenr?: string; displayAktId: string }
): number {
   const aDate = parseDatoLoebenrDate(a.datoLoebenr || a.displayAktId);
   const bDate = parseDatoLoebenrDate(b.datoLoebenr || b.displayAktId);
   if (aDate !== bDate) return aDate - bDate;
   return (a.datoLoebenr || a.displayAktId).localeCompare(b.datoLoebenr || b.displayAktId);
}

function parseDatoLoebenrDate(value: string): number {
   const trimmed = value.trim();
   const danishDate = /^(\d{2})\.(\d{2})\.(\d{4})/.exec(trimmed);
   if (danishDate) {
      return toValidUtcDate(Number(danishDate[3]), Number(danishDate[2]), Number(danishDate[1]));
   }
   const compactDanishDate = /^(\d{2})(\d{2})(\d{4})/.exec(trimmed);
   if (compactDanishDate) {
      const date = toValidUtcDate(Number(compactDanishDate[3]), Number(compactDanishDate[2]), Number(compactDanishDate[1]));
      if (date !== Number.MAX_SAFE_INTEGER) return date;
   }
   const compactIsoDate = /^(\d{4})(\d{2})(\d{2})/.exec(trimmed);
   if (compactIsoDate) {
      return toValidUtcDate(Number(compactIsoDate[1]), Number(compactIsoDate[2]), Number(compactIsoDate[3]));
   }
   return Number.MAX_SAFE_INTEGER;
}

function toValidUtcDate(year: number, month: number, day: number): number {
   if (year < 1800 || year > 2200 || month < 1 || month > 12 || day < 1 || day > 31) {
      return Number.MAX_SAFE_INTEGER;
   }
   return Date.UTC(year, month - 1, day);
}

function buildServitutMatrixExcelBlob(payload: ServitutRedegoerelsePayload, treatedKeys: Set<string>): Blob {
   const rows = buildServitutMatrixExcelRows(payload, treatedKeys);
   const worksheet = XlsxUtils.json_to_sheet(rows);
   const workbook = XlsxUtils.book_new();
   XlsxUtils.book_append_sheet(workbook, worksheet, "Servitutredegørelse");
   const buffer = writeXlsx(workbook, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
   return new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

function buildServitutMatrixExcelRows(payload: ServitutRedegoerelsePayload, treatedKeys: Set<string>) {
   const hasCaseDescription = Boolean(payload.caseDescription?.trim());
   return sortServitutRows(payload.matrix.rows, payload.matrix.columns, "default").map((row) => {
      const handling = row.handling ?? "legacyLlmPdf";
      const reviewTriage = getReviewTriage(row);
      const record: Record<string, string | number> = {
         "Dato løbenr": row.datoLoebenr || row.displayAktId,
         "Prioritet": row.priority,
         "Dokumenttype": handling === "electronicZipPackage" ? "Digital tingbog" : "Indskannet akt",
         "AI sider": handling === "electronicZipPackage"
            ? ""
            : (row.analysisItem?.pages_kept?.length ? formatPageRanges(row.analysisItem.pages_kept) : ""),
         "Gemmes sider": handling === "electronicZipPackage"
            ? "Akt + SERVIMAP + bilag (ved eksport)"
            : (row.selectedPages?.length ? formatPageRanges(row.selectedPages) : ""),
         "Behandlet": treatedKeys.has(row.key) ? "x" : "",
         "AI resume": row.analysisItem?.summary || "",
         "AI samlet sikkerhed": reviewTriage.label,
         "AI samlet sikkerhed begrundelse": row.analysisItem?.overall_confidence_reasoning || "",
         "Kræver tjek": row.analysisItem?.needs_review ? "x" : "",
         "Tjek årsager": row.analysisItem?.review_reasons?.join("; ") || "",
         "Servituttype": formatServitutType(row.analysisItem?.servitut_type),
         "Servituttype sikkerhed": formatConfidence(row.analysisItem?.servitut_type_confidence),
         "Påtaleberettiget": row.analysisItem?.paataleberettiget || "",
         "Påtaleberettiget sikkerhed": formatConfidence(row.analysisItem?.paataleberettiget_confidence),
         "Case relevant": hasCaseDescription ? formatCaseRelevant(row.analysisItem?.case_relevant) : "",
         "Case begrundelse": row.analysisItem?.case_relevance_reasoning || "",
         "Case sikkerhed": formatConfidence(row.analysisItem?.case_relevance_confidence),
         "Servitut": row.summary,
         "PDF status": row.pdfStatus,
         "PDF kilde": row.pdfSource || "",
      };
      payload.matrix.columns.forEach((bfe) => {
         record[getServitutMatrixColumnLabel(payload, bfe)] = row.includedBfes.includes(bfe) ? "x" : "";
      });
      return record;
   });
}

async function buildServitutRedegoerelseZip(
   payload: ServitutRedegoerelsePayload,
   treatedKeys: Set<string>,
   onProgress?: (state: ServitutRedegoerelseExportProgress) => void | Promise<void>,
): Promise<Blob> {
   const zip = new JSZip();
   const usedNames = new Set<string>();
   const analyzer = getANNLLMAnalyzer();
   const rows = sortServitutRows(
      payload.matrix.rows.filter((row) => treatedKeys.has(row.key)),
      payload.matrix.columns,
      "default"
   );
   const totalSteps = 2 + rows.length;

   const emit = async (state: ServitutRedegoerelseExportProgress): Promise<void> => {
      await onProgress?.(state);
   };

   let completedSteps = 0;
   await emit({
      completedSteps,
      totalSteps,
      stage: "spreadsheet",
      activeRowKey: null,
   });

   zip.file(
      createUniqueZipEntryName("Servitutredegørelse.xlsx", usedNames),
      buildServitutMatrixExcelBlob(payload, treatedKeys),
   );
   completedSteps += 1;
   await emit({
      completedSteps,
      totalSteps,
      stage: "spreadsheet",
      activeRowKey: null,
   });

   for (let docIndex = 0; docIndex < rows.length; docIndex++) {
      const row = rows[docIndex];
      await emit({
         completedSteps,
         totalSteps,
         stage: "document",
         activeRowKey: row.key,
         documentIndex: docIndex + 1,
         documentTotal: rows.length,
      });

      const handling = row.handling ?? "legacyLlmPdf";
      if (handling === "electronicZipPackage") {
         const baseFileName = row.exportFileName || `${row.displayAktId || row.key}.pdf`;
         if (!row.electronicServitut) {
            zip.file(
               createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
               `Digital servitutpakke ikke tilgængelig (${row.displayAktId}).\n${row.summary || ""}`,
            );
         } else {
            try {
               const packageResult = await addServitutPackageToZip(zip, row.electronicServitut);
               if (packageResult.warnings.length) {
                  console.warn("[Servitutredegørelse] digital pakke-advarsler", row.key, packageResult.warnings);
               }
            } catch (error) {
               zip.file(
                  createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
                  `Kunne ikke hente digital servitutpakke: ${error instanceof Error ? error.message : String(error)}\n\n${row.summary || "Ingen servituttekst."}`,
               );
            }
         }
      } else {
         const pagesToKeep = normalizePages(row.selectedPages || []);
         const baseFileName = row.exportFileName || `${row.displayAktId || row.key}.pdf`;
         if (!pagesToKeep.length) {
            zip.file(
               createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
               buildMissingServitutText(row),
            );
         } else if (!row.aktId) {
            zip.file(
               createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
               `${buildMissingServitutText(row)}\n\nPDF kunne ikke hentes, fordi akt-id mangler.`,
            );
         } else {
            try {
               const pdfBlob = await analyzer.fetchPdf(row.aktId);
               const cutPdfBlob = await cutPdfToSelectedPages(pdfBlob, pagesToKeep);
               zip.file(createUniqueZipEntryName(baseFileName, usedNames), cutPdfBlob);
            } catch (error) {
               zip.file(
                  createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
                  `${buildMissingServitutText(row)}\n\nPDF kunne ikke klippes: ${error instanceof Error ? error.message : String(error)}`,
               );
            }
         }
      }

      completedSteps += 1;
   }

   await emit({
      completedSteps,
      totalSteps,
      stage: "compress",
      activeRowKey: null,
   });

   const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
   completedSteps += 1;
   await emit({
      completedSteps,
      totalSteps,
      stage: "compress",
      activeRowKey: null,
   });

   return blob;
}

function buildMissingServitutText(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): string {
   return [
      `Could not find servitut: ${row.displayAktId}`,
      `Prioritet: ${row.priority || "-"}`,
      `AI forslag: ${row.analysisItem?.pages_kept?.length ? formatPageRanges(row.analysisItem.pages_kept) : "Ingen sider"}`,
      "",
      row.summary || "Ingen servituttekst.",
   ].join("\n");
}

function toTxtFileName(fileName: string): string {
   return fileName.replace(/\.[^.]+$/i, "") + ".txt";
}

function createUniqueZipEntryName(fileName: string, usedNames: Set<string>): string {
   const trimmed = fileName.trim() || "servitut.pdf";
   if (!usedNames.has(trimmed)) {
      usedNames.add(trimmed);
      return trimmed;
   }
   const match = /^(.*?)(\.[^.]+)?$/.exec(trimmed);
   const baseName = match?.[1] || "servitut";
   const extension = match?.[2] || "";
   let index = 2;
   while (usedNames.has(`${baseName} (${index})${extension}`)) {
      index += 1;
   }
   const uniqueName = `${baseName} (${index})${extension}`;
   usedNames.add(uniqueName);
   return uniqueName;
}

function groupMetadataByAkt(metadata: LLMMetadata[]): LLMMetadata[][] {
   const batchesByAkt = new Map<string, LLMMetadata[]>();
   metadata.forEach((entry) => {
      const key = entry.documentUrl || entry.id || entry.aktId || entry.document;
      const batch = batchesByAkt.get(key) ?? [];
      batch.push(entry);
      batchesByAkt.set(key, batch);
   });
   return Array.from(batchesByAkt.values());
}

async function runWithConcurrency<T>(
   items: T[],
   concurrency: number,
   worker: (item: T) => Promise<void>
) {
   let nextIndex = 0;
   const workerCount = Math.min(concurrency, items.length);
   const runners = Array.from({ length: workerCount }, async () => {
      while (nextIndex < items.length) {
         const item = items[nextIndex];
         nextIndex += 1;
         await worker(item);
      }
   });
   await Promise.all(runners);
}

function attachAnalysisResult(payload: ServitutRedegoerelsePayload, result: LLMAnalysisResult): ServitutRedegoerelsePayload {
   const byId = new Map<string, LLMAnalysisResultItem>();
   const byAktId = new Map<string, LLMAnalysisResultItem>();
   result.items?.forEach((item) => {
      if (item.id) {
         byId.set(item.id, item);
      }
      if (item.aktId) {
         byAktId.set(item.aktId, item);
      }
   });
   return {
      ...payload,
      analysisResult: result,
      matrix: {
         ...payload.matrix,
         rows: payload.matrix.rows.map((row) => {
            if ((row.handling ?? "legacyLlmPdf") === "electronicZipPackage") {
               const analysisItem = byId.get(row.key) ?? row.analysisItem;
               return { ...row, analysisItem };
            }
            const analysisItem = byId.get(row.key) ?? byAktId.get(row.analysisAktId) ?? row.analysisItem;
            return {
               ...row,
               analysisItem,
               selectedPages: row.selectedPages ?? (analysisItem ? normalizePages(analysisItem.pages_kept || []) : row.selectedPages),
            };
         }),
      },
   };
}

